"use client";

import { cn } from "@/lib/utils";
import { useRouteAnnouncement } from "@/lib/a11y/use-route-announcement";

/**
 * The one `<main>` landmark, plus the live region that makes client-side
 * navigation perceivable (NFR-32).
 *
 * `tabIndex={-1}` is what makes the skip link and the route-change focus move
 * work: a <main> is not focusable by default, so `focus()` on it is a no-op and
 * both mechanisms silently do nothing.
 */
export function AppMain({
  children,
  className,
  id = "main-content",
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  const announcement = useRouteAnnouncement(id);

  return (
    <>
      <main id={id} tabIndex={-1} className={cn("flex-1 overflow-auto focus:outline-none", className)}>
        {children}
      </main>
      {/*
        Off-screen and polite: it must not interrupt whatever the reader is
        saying, and it must not be visible to anyone else.
      */}
      <p aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </p>
    </>
  );
}
